/**
 * 24. Customer Invoices — list with statuses; create from sales orders;
 * credit notes; print/e-invoice.
 */
import {
  Card,
  PageHeader,
  DataTable,
  Skeleton,
  StatusBadge,
} from "@/components/ui";
import { useTranslation } from "@/lib/i18n";
import { PendingButton } from "@/components/PendingAction";
import { useApiList } from "@/lib/api/hooks";
import { formatMoney, formatDate } from "@/lib/money";

interface CustomerInvoiceRow {
  id: string;
  invoiceNumber: string;
  invoiceDate: string;
  dueDate: string | null;
  customerName: string;
  status:
    | "Draft"
    | "Approved"
    | "Posted"
    | "Sent"
    | "Paid"
    | "Overdue"
    | "WrittenOff";
  total: string;
  currencyCode: string;
}

export function CustomerInvoicesPage() {
  const { t } = useTranslation();
  const { data, isLoading } = useApiList<CustomerInvoiceRow>(
    ["finance", "customer-invoices"],
    "/finance/customer-invoices",
  );

  return (
    <div>
      <PageHeader
        title="Customer Invoices"
        subtitle="Create from sales orders, credit notes, print and e-invoice."
        actions={
          <PendingButton variant="primary" reason="invoiceEditor" slice="ED-W4">
            {t("documentActions.newSalesInvoice")}
          </PendingButton>
        }
      />
      {isLoading || !data ? (
        <Skeleton rows={10} />
      ) : (
        <Card title={`Invoices (${data.total})`}>
          <DataTable<CustomerInvoiceRow>
            rows={data.items}
            columns={[
              {
                key: "invoiceNumber",
                label: "Number",
                render: (r) => (
                  <span className="atlas-mono">{r.invoiceNumber}</span>
                ),
              },
              {
                key: "invoiceDate",
                label: "Date",
                render: (r) => formatDate(r.invoiceDate),
              },
              {
                key: "dueDate",
                label: "Due",
                render: (r) => (r.dueDate ? formatDate(r.dueDate) : "—"),
              },
              { key: "customerName", label: "Customer" },
              {
                key: "total",
                label: "Total",
                numeric: true,
                render: (r) => formatMoney(r.total, r.currencyCode),
              },
              {
                key: "status",
                label: "Status",
                render: (r) => <StatusBadge status={r.status} />,
              },
            ]}
          />
        </Card>
      )}
    </div>
  );
}
